/* eslint-disable max-len */
import { CircularProgress, Grid, makeStyles } from '@material-ui/core';
import { Entity } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { EntityDetailsUIComponentSettings, EntityExclusionCustomSettings, EntityUIComponentCollection,
    filterEntityClassesByEntityClassesExclusionCustomSettings, filterEntityInstancesByEntityInstancesExclusionCustomSettings, GridCustomSettings,
    isEntityExclusionCustomSettings, isGridCustomSettings } from '..';
import { ExtGrid } from '../..';
import { useSelectAllEntityChildren } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingStringValue } from '../../../utils/utils';
import { EntityUIComponentWithChildrenProps } from '../EntityDetailsLayouts';


const useStyle = makeStyles(() => {
    return {
        root: {
        },
    };
}, { name: 'EntityChildrenListUIComponent', index: 1 });



/**
 * Interface for EntityChildrenListUIComponentCustomSettings's customSettings. These are the props/value that can be
 * configured for the 'EntityChildrenListUIComponentCustomSettings' from the entityClass's uiSettings.
 */
export interface EntityChildrenListUIComponentCustomSettings extends EntityExclusionCustomSettings, GridCustomSettings {

    // Whether or not to handle child component click event and take the user to the child's details page. Default is TRUE unless set to false.
    readonly handleChildClick?: boolean | false;

    /**
     * The EntityUIComponent to use for rendering the children. This can be either string name of an entity UI Component or an
     * Object that contain customization for the component e.g.
     *      customSettings: {
     *          includeEntityClasses: [...],
     *          excludeEntityClasses: [...],
     *          childrenUIComponent: "Entity_Basic_View_Comp"       // Use 'Entity_Basic_View_Comp' to display children without any customization
     *      }
     *  OR
     *      customSettings: {
     *          includeEntityClasses: [...],
     *          excludeEntityClasses: [...],
     *          childrenUIComponent: {
     *              componentType: "Entity_Image_Comp",             // Use 'Entity_Image_Comp' to display children
     *              customSettings: {                               // And customize the child component 'Entity_Image_Comp' with these config
     *                  containerStyle: {
     *                      background: 'white',
     *                      padding   : '10px',
     *                  },
     *              }
     *          }
     *      }
     */
    readonly childrenUIComponent?: (string | EntityDetailsUIComponentSettings) | undefined;
}



/**
 * type guard function to check if an object is a type of EntityChildrenListUIComponentCustomSettings
 */
const isEntityChildrenListUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityChildrenListUIComponentCustomSettings => {
    const definedComponentTypes = EntityUIComponentCollection.getInstance().getAllComponentTypes();

    const settings = obj as EntityChildrenListUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.handleChildClick)) {
        return errorLogger?.logError({
            message: '"handleChildClick" must be undefined or a boolean.',
            value  : settings.handleChildClick,
            path   : [...configPath, 'handleChildClick'],
        }) ?? false;
    }

    // childrenUIComponent is optional but if it is provided, it must be a string name of one of the existing EntityUIComponent
    if (settings.childrenUIComponent !== undefined) {
        if (typeof settings.childrenUIComponent !== 'string' && !isObjectLiteral(settings.childrenUIComponent)) {
            return errorLogger?.logError({
                message: '"childrenUIComponent" must be undefined, a string, or an object.',
                value  : settings.childrenUIComponent,
                path   : [...configPath, 'childrenUIComponent'],
            }) ?? false;
        }

        if (isValidSettingStringValue(settings.childrenUIComponent)) {
            if (!isValidSettingStringValue(settings.childrenUIComponent, true, false, definedComponentTypes)) {
                return errorLogger?.logError({
                    message: '"childrenUIComponent" must be the name of one of the existing EntityUIComponents.',
                    value  : settings.childrenUIComponent,
                    path   : [...configPath, 'childrenUIComponent'],
                }) ?? false;
            }
        }

        if (isObjectLiteral(settings.childrenUIComponent)) {
            // Find the componentInfo object from the EntityUIComponentCollection with the given settings.childrenUIComponent.componentType
            const childrenComponentInfo = EntityUIComponentCollection.getInstance().getComponent(settings.childrenUIComponent.componentType);

            // if "componentInfo" is undefined then that mean the "componentType" does not exist
            if (!childrenComponentInfo) {
                return errorLogger?.logError({
                    message: '"componentType" must be the name of one of the existing EntityUIComponents.',
                    value  : settings.childrenUIComponent.componentType,
                    path   : [...configPath, 'childrenUIComponent', 'componentType'],
                }) ?? false;
            }

            // If "settings.childrenUIComponent.customSettings" is OPTIONAL but if it is provided, it must be valid
            const childrenComponentCustomSettings = settings.childrenUIComponent.customSettings;

            // customSettings is OPTIONAL but if it is provided AND the childrenComponentInfo has a validator for the customSettings then call
            // the validator to validate the customSettings for the childrenComponentInfo.
            if (
                childrenComponentCustomSettings !== undefined
                && childrenComponentInfo.customSettingsValidator
                && !childrenComponentInfo.customSettingsValidator(
                    childrenComponentCustomSettings, errorLogger, [...configPath, 'childrenUIComponent', 'customSettings'],
                )
            ) {
                // Failed to validate the customSettings
                return false;
            }
        }
    }

    if (!isEntityExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    if (!isGridCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};



export interface EntityChildrenListUIComponentProps extends EntityUIComponentWithChildrenProps {
    readonly className?: string | undefined;
    readonly customSettings?: EntityChildrenListUIComponentCustomSettings | undefined;
}


export const EntityChildrenListUIComponent: React.FC<EntityChildrenListUIComponentProps> = (
    props: EntityChildrenListUIComponentProps,
) => {

    // Get all children entities
    const childEntities = useSelectAllEntityChildren(
        props.projectId,
        props.entityId,
    );

    // Filter out the entities that are NOT instances of the entity classes specified in includeEntityClasses
    const filteredChildEntities = useMemo(() => {
        if (childEntities) {
            const childrenFilteredByEntityClasses = filterEntityClassesByEntityClassesExclusionCustomSettings(childEntities, props.customSettings);
            return filterEntityInstancesByEntityInstancesExclusionCustomSettings(childrenFilteredByEntityClasses, props.customSettings);
        }

        return undefined;
    }, [childEntities, props.customSettings]);

    const classes = useStyle();


    /**
     * Create an EntityUIComponent to display a child entity.
     */
    const createChildEntityUIComponent = useCallback((childEntity: Entity, comp: string | EntityDetailsUIComponentSettings | undefined) => {

        // if 'comp' is an Object then expect it to have 'componentType' attribute which will be a string name of the entity UI component
        const compType = (typeof comp === 'object' ? comp.componentType : comp) ?? 'Entity_Basic_View_Comp';
        const componentInfo = EntityUIComponentCollection.getInstance().getComponent(compType);


        if (!componentInfo) {
            return (
                <div>
                    {`Unknown Component Type '${compType}'`}
                </div>
            );
        }

        const Component = componentInfo.componentFunc;
        const customSettings = isObjectLiteral(comp) ? comp.customSettings : undefined;


        return (
            <Component
                projectId={props.projectId}
                entityId={childEntity.entityId}
                customSettings={customSettings}
                fetchDataParams={props.fetchDataParams}
                onShowEntityDetails={props.onShowEntityDetails}
                onShowEntityPreview={props.onShowEntityPreview}
                onClick={props.customSettings?.handleChildClick !== false ? (event) => {
                    if (event) {
                        event.stopPropagation();
                        event.preventDefault();
                    }
                    if (props.onShowEntityDetails) {
                        props.onShowEntityDetails(childEntity);
                    }
                } : undefined}
            />
        );
    }, [props]);



    return (
        <>
            {filteredChildEntities && filteredChildEntities.length > 0 && (
                <ExtGrid
                    className={clsx(props.className, classes.root)}
                    container
                    xs_spacing={1}
                    md_spacing={2}
                    lg_spacing={3}
                >
                    {filteredChildEntities.map((child) => {
                        return (
                            <Grid
                                key={`EntityLatestMetricValueViewContainer-${child.entityId}`}
                                item
                                xs={props.customSettings?.gridSizes?.xs ?? 12}
                                sm={props.customSettings?.gridSizes?.sm}
                                md={props.customSettings?.gridSizes?.md}
                                lg={props.customSettings?.gridSizes?.lg}
                                xl={props.customSettings?.gridSizes?.xl}
                            >
                                {createChildEntityUIComponent(child, props.customSettings?.childrenUIComponent)}
                            </Grid>
                        );
                    })}
                </ExtGrid>
            )}

            {!childEntities && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityChildrenListUIComponent', EntityChildrenListUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Children_List_Comp',
    {
        componentFunc          : EntityChildrenListUIComponent,
        customSettingsValidator: isEntityChildrenListUIComponentCustomSettings,
    },
);
